import { initializeApp } from 'firebase/app';
import { connectAuthEmulator, getAuth } from 'firebase/auth';
import { connectFirestoreEmulator, getFirestore } from 'firebase/firestore';
import { getAnalytics, isSupported } from "firebase/analytics";

// Firebase configuration
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_APP_MEASUREMENT_ID
};

// Validate Firebase config
const validateConfig = () => {
  const requiredFields = ['apiKey', 'authDomain', 'projectId', 'appId'];
  const missingFields = requiredFields.filter(
    field => !firebaseConfig[field as keyof typeof firebaseConfig]
  );

  if (missingFields.length > 0) {
    console.error('Missing Firebase configuration fields:', missingFields);
    console.error('Please check your .env file and ensure all VITE_FIREBASE_* variables are set.');
  }
};

validateConfig();

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize Analytics only if supported (not in SSR or unsupported browsers)
export let analytics: ReturnType<typeof getAnalytics> | null = null;
isSupported().then(supported => {
  if (supported) {
    analytics = getAnalytics(app);
  }
}).catch(() => {
  // Analytics not supported
});

// Initialize Firebase Authentication
export const auth = getAuth(app);

// Initialize Firestore (kept for legacy callers; production code MUST go
// through the FastAPI backend — see /src/api/client.ts).
export const db = getFirestore(app);

// Connect to local emulators in dev when explicitly opted in via env.
const useEmulators = import.meta.env.VITE_USE_EMULATORS === 'true';
console.log(`[Firebase Init] VITE_USE_EMULATORS=${import.meta.env.VITE_USE_EMULATORS} (${typeof import.meta.env.VITE_USE_EMULATORS}) → useEmulators=${useEmulators}`);
console.log(`[Firebase Init] Project: ${firebaseConfig.projectId}`);

if (useEmulators && typeof window !== 'undefined') {
  console.log('[Firebase Init] 🔌 Connecting to local emulators (Auth :9099, Firestore :8080)');
  // Idempotent: subsequent calls are no-ops once the emulator is wired.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if (!(auth as any)._candezaEmulatorWired) {
    connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (auth as any)._candezaEmulatorWired = true;
    console.log('[Firebase Init] ✅ Auth emulator connected');
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if (!(db as any)._candezaEmulatorWired) {
    try {
      connectFirestoreEmulator(db, 'localhost', 8080);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (db as any)._candezaEmulatorWired = true;
      console.log('[Firebase Init] ✅ Firestore emulator connected');
    } catch {
      /* already connected */
    }
  }
} else {
  console.log('[Firebase Init] ☁️ Using production Firebase (emulators disabled)');
}

export default app;

